/** Standard EAN code prefix for Belgium */
export var EAN_PREFIX = "54";
/**
 * Truncate EAN code by removing the standard prefix
 */
export var truncateEAN = function (eanCode) {
    return (eanCode === null || eanCode === void 0 ? void 0 : eanCode.startsWith(EAN_PREFIX))
        ? eanCode.replace(EAN_PREFIX, "")
        : eanCode || undefined;
};
/**
 * Prepend EAN prefix to a truncated EAN code
 */
export var prependEANPrefix = function (eanCode) {
    if (!eanCode)
        return undefined;
    return eanCode.startsWith(EAN_PREFIX) ? eanCode : "".concat(EAN_PREFIX).concat(eanCode);
};
/**
 * Predefined amenity name constants used throughout the application.
 * These follow the naming convention: rentio.default.<snake_case_field_name>
 */
export var AMENITY_NAMES = {
    // Exterior
    buildingAdjoined: "rentio.default.building_adjoined",
    buildingSurfaceArea: "rentio.default.building_surface_area",
    buildingHasHardMaterials: "rentio.default.building_has_hard_materials",
    // Interior
    buildingFloor: "rentio.default.building_floor",
    buildingFloorCount: "rentio.default.building_floor_count",
    roomCountBedroom: "rentio.default.room_count_bedroom",
    roomCountBathroom: "rentio.default.room_count_bathroom",
    roomCountGarage: "rentio.default.room_count_garage",
    buildingHasStorage: "rentio.default.building_has_storage",
    isFurnished: "rentio.default.is_furnished",
    // Amenities
    hasOven: "rentio.default.has_oven",
    hasFridge: "rentio.default.has_fridge",
    hasDishwasher: "rentio.default.has_dishwasher",
    hasRangeHood: "rentio.default.has_range_hood",
    hasLift: "rentio.default.has_lift",
    // Energy
    hasDoubleGlazing: "rentio.default.has_double_glazing",
    hasCentralHeating: "rentio.default.has_central_heating",
    heatingType: "rentio.default.heating_type",
};
/**
 * Get the raw value of an amenity by name
 */
export var getAmenityValue = function (amenities, name) {
    if (!amenities)
        return undefined;
    var amenity = amenities.find(function (a) { return a.name === name; });
    return amenity === null || amenity === void 0 ? void 0 : amenity.value;
};
/**
 * Get an amenity value as a boolean
 */
export var getAmenityBooleanValue = function (amenities, name) {
    var value = getAmenityValue(amenities, name);
    if (value === undefined)
        return null;
    if (typeof value === "boolean")
        return value;
    if (typeof value === "string")
        return value === "true";
    return null;
};
/**
 * Get an amenity value as a number
 */
export var getAmenityNumberValue = function (amenities, name) {
    var value = getAmenityValue(amenities, name);
    if (value === undefined || value === null)
        return null;
    if (typeof value === "number")
        return value;
    if (typeof value === "string") {
        var parsed = parseFloat(value);
        return isNaN(parsed) ? null : parsed;
    }
    return null;
};
/**
 * Get an amenity value as a string
 */
export var getAmenityStringValue = function (amenities, name) {
    var value = getAmenityValue(amenities, name);
    if (value === undefined || value === null)
        return null;
    return String(value);
};
/**
 * Create an amenity item for upsert operation.
 * Handles deletion by setting value to null when the value is undefined/null
 * and there's an existing amenity.
 *
 * @param propertyAmenities - Existing property amenities
 * @param name - Amenity name
 * @param value - New value (null/undefined triggers deletion if amenity exists)
 * @param dataType - Data type of the amenity
 * @returns IUpsertPropertyAmenityItem or null if no action needed
 */
export var createAmenityItem = function (propertyAmenities, name, value, dataType) {
    var existingAmenity = propertyAmenities === null || propertyAmenities === void 0 ? void 0 : propertyAmenities.find(function (a) { return a.name === name; });
    // If value is undefined and no existing amenity, don't include
    if (value === undefined && !existingAmenity)
        return null;
    // If value is undefined but there's an existing amenity, send null to delete it
    if (value === undefined && existingAmenity) {
        return {
            id: existingAmenity.id,
            name: name,
            dataType: dataType,
            value: null,
        };
    }
    // If value is null (explicitly cleared) and there's an existing amenity, send null to delete
    if (value === null && existingAmenity) {
        return {
            id: existingAmenity.id,
            name: name,
            dataType: dataType,
            value: null,
        };
    }
    // If value is null/undefined and no existing amenity, don't include
    if (value === null || value === undefined)
        return null;
    return {
        id: existingAmenity === null || existingAmenity === void 0 ? void 0 : existingAmenity.id,
        name: name,
        dataType: dataType,
        value: value,
    };
};
/**
 * Determine the data type from a value
 */
export var getDataTypeFromValue = function (value) {
    if (typeof value === "boolean")
        return "boolean";
    if (typeof value === "number")
        return "number";
    return "string";
};
/**
 * Check if an amenity name is a predefined default amenity
 */
export var isPredefinedAmenity = function (name) {
    return name.startsWith("rentio.default.");
};
